import React, { useEffect, useState } from "react";
import ct from "class-types.macro";
import PaginationItem from "./PaginationItem";
import {
  linkClasses,
  currentLinkClasses,
  listClasses,
  listItemClasses,
} from "./Pagination.styles";

export interface Props {
  currentPage: number;
  onChange?: ((page: number, pageSize: number) => void) | undefined;
  total: number;
  pageSize: number;
  createHREF?: (page: number) => string;
}

const Pagination = ({
  currentPage,
  onChange,
  total,
  pageSize,
  createHREF,
}: Props) => {
  const productlistingPageLimit = 100;

  const totalPages = Math.min(
    Math.ceil(total / pageSize),
    productlistingPageLimit,
  );

  const [pagerList, setPagerList] = useState<
    {
      name: string | number;
      value: number;
    }[]
  >([]);

  useEffect(() => {
    const newPagerList = [];
    // display first five pagers if currentPage is within first 4 pagers
    if (totalPages > 7 && currentPage <= 4) {
      for (let page = 1; page < currentPage; page += 1) {
        newPagerList.push({ name: page, value: page });
      }
      newPagerList.push({ name: currentPage, value: currentPage });
      for (let page = currentPage + 1; page < 6; page += 1) {
        newPagerList.push({ name: page, value: page });
      }
      // \u2026 is the unicode for a horizontal ellipsis
      newPagerList.push({ name: "\u2026", value: 6 });
      newPagerList.push({ name: totalPages, value: totalPages });
    }
    // display last five pagers if currentPage is within last 4 pagers
    else if (totalPages > 7 && currentPage >= totalPages - 3) {
      newPagerList.push({ name: 1, value: 1 });
      newPagerList.push({ name: "\u2026", value: totalPages - 5 });
      for (let page = totalPages - 4; page < currentPage; page += 1) {
        newPagerList.push({ name: page, value: page });
      }
      newPagerList.push({ name: currentPage, value: currentPage });
      for (let page = currentPage; page < totalPages; page += 1) {
        newPagerList.push({ name: page + 1, value: page + 1 });
      }
    }
    // display ellipses based on current page location
    else {
      if (totalPages > 7 && currentPage - 3 > 1) {
        newPagerList.push({ name: 1, value: 1 });
        newPagerList.push({ name: "\u2026", value: currentPage - 3 });
        newPagerList.push({ name: currentPage - 2, value: currentPage - 2 });
        newPagerList.push({ name: currentPage - 1, value: currentPage - 1 });
      } else {
        for (let page = 1; page < currentPage; page += 1) {
          newPagerList.push({ name: page, value: page });
        }
      }

      newPagerList.push({ name: currentPage, value: currentPage });

      if (totalPages > 7 && currentPage + 3 < totalPages) {
        newPagerList.push({ name: currentPage + 1, value: currentPage + 1 });
        newPagerList.push({ name: currentPage + 2, value: currentPage + 2 });
        newPagerList.push({ name: "\u2026", value: currentPage + 3 });
        newPagerList.push({ name: totalPages, value: totalPages });
      } else {
        for (let page = currentPage; page < totalPages; page += 1) {
          newPagerList.push({ name: page + 1, value: page + 1 });
        }
      }
    }
    setPagerList(newPagerList);
  }, [totalPages, currentPage]);

  return (
    <nav aria-label="pagination">
      {totalPages > 1 ? (
        <ul
          className={listClasses}
          style={{ boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.15)" }}
        >
          {pagerList.length > 1 && currentPage !== 1 ? (
            <PaginationItem
              name="previous"
              key={`go to page ${currentPage - 1}`}
              listItemClasses={ct(listItemClasses, "rounded-l-md")}
              linkClasses={ct(linkClasses, "rounded-l-md", "text-gray-800")}
              ariaLabel={`go to page ${currentPage - 1}`}
              targetPage={currentPage - 1}
              totalPages={totalPages}
              createHREF={createHREF}
              onChange={onChange}
              icon="left-open"
            />
          ) : null}
          {pagerList.map((page) => {
            if (page.value === currentPage) {
              if (page.value === 1) {
                return (
                  <PaginationItem
                    name={page.name}
                    key={`current page, page ${page.value}`}
                    listItemClasses={ct(listItemClasses, "rounded-l-md")}
                    linkClasses={ct(
                      linkClasses,
                      currentLinkClasses,
                      "rounded-l-md",
                      "border-l",
                    )}
                    ariaLabel={`current page, page ${page.value}`}
                    targetPage={page.value}
                    totalPages={totalPages}
                    createHREF={createHREF}
                    onChange={onChange}
                  />
                );
              }

              if (page.value === totalPages) {
                return (
                  <PaginationItem
                    name={page.name}
                    key={`current page, page ${page.value}`}
                    listItemClasses={ct(listItemClasses, "rounded-r-md")}
                    linkClasses={ct(
                      linkClasses,
                      currentLinkClasses,
                      "rounded-r-md",
                      "border-r",
                    )}
                    ariaLabel={`current page, page ${page.value}`}
                    targetPage={page.value}
                    totalPages={totalPages}
                    createHREF={createHREF}
                    onChange={onChange}
                  />
                );
              }

              return (
                <PaginationItem
                  name={page.name}
                  key={`current page, page ${page.value}`}
                  listItemClasses={listItemClasses}
                  linkClasses={ct(linkClasses, currentLinkClasses)}
                  ariaLabel={`current page, page ${page.value}`}
                  targetPage={page.value}
                  totalPages={totalPages}
                  createHREF={createHREF}
                  onChange={onChange}
                />
              );
            }

            if (page.name === "\u2026") {
              return (
                <PaginationItem
                  name={page.name}
                  key={`go to page ${page.value}`}
                  listItemClasses={listItemClasses}
                  linkClasses={ct(linkClasses, "text-gray-800")}
                  ariaLabel={`go to page ${page.value}`}
                  targetPage={page.value}
                  totalPages={totalPages}
                  createHREF={createHREF}
                  onChange={onChange}
                />
              );
            }

            if (page.value === totalPages) {
              return (
                <PaginationItem
                  name={page.name}
                  key={`last page, page ${page.value}`}
                  listItemClasses={listItemClasses}
                  linkClasses={ct(linkClasses, "text-gray-800")}
                  ariaLabel={`last page, page ${page.value}`}
                  targetPage={page.value}
                  totalPages={totalPages}
                  createHREF={createHREF}
                  onChange={onChange}
                />
              );
            }

            return (
              <PaginationItem
                name={page.name}
                key={`page ${page.value}`}
                listItemClasses={listItemClasses}
                linkClasses={ct(linkClasses, "text-gray-800")}
                ariaLabel={`page ${page.value}`}
                targetPage={page.value}
                totalPages={totalPages}
                createHREF={createHREF}
                onChange={onChange}
              />
            );
          })}
          {pagerList.length > 1 && currentPage !== totalPages ? (
            <PaginationItem
              name="next"
              key={`go to page ${currentPage + 1}`}
              listItemClasses={ct(listItemClasses, "rounded-r-md")}
              linkClasses={ct(
                linkClasses,
                "rounded-r-md",
                "text-gray-800",
                "border-r",
              )}
              ariaLabel={`go to page ${currentPage + 1}`}
              targetPage={currentPage + 1}
              totalPages={totalPages}
              createHREF={createHREF}
              onChange={onChange}
              icon="right-open"
            />
          ) : null}
        </ul>
      ) : (
        <p className="sr-only">No additional pages</p>
      )}
    </nav>
  );
};

export default Pagination;
